import { useState, useEffect } from "react";
import {
  Wallet,
  CreditCard,
  Banknote,
  UtensilsCrossed,
  Truck,
  Calendar as CalendarIcon,
  RefreshCw,
  Clock,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  User,
  Download,
  FileSpreadsheet,
  FileText,
  Mail,
  Send,
  ShoppingBag,
  Play,
  Square,
  Printer,
  AlertCircle,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import API from "@/utils/api";
import printService from "@/services/PrintService";

export default function AdminCashReport() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  // Load branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axios.get(`${API}/admin/branches`, { withCredentials: true });
        setBranches(res.data.filter(b => b.active));

        // Pre-select based on user profile
        if (user?.assigned_branches?.length > 0) {
          setSelectedBranch(user.assigned_branches[0]);
        }
      } catch (error) {
        console.error("Error loading branches");
      }
    };
    fetchBranches();
  }, [user]);
  const [emailToSend, setEmailToSend] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [configEmail, setConfigEmail] = useState("");

  // Shift management state
  const [activeShift, setActiveShift] = useState(null);
  const [showOpenShiftDialog, setShowOpenShiftDialog] = useState(false);
  const [showCloseShiftDialog, setShowCloseShiftDialog] = useState(false);
  const [initialCash, setInitialCash] = useState("");
  const [finalCash, setFinalCash] = useState("");
  const [shiftNotes, setShiftNotes] = useState("");
  const [shiftReport, setShiftReport] = useState(null);
  const [showShiftReportDialog, setShowShiftReportDialog] = useState(false);

  // Check for active shift
  const fetchActiveShift = async () => {
    try {
      const branchParam = selectedBranch && selectedBranch !== "all" ? `?branch_id=${selectedBranch}` : "";
      const response = await axios.get(`${API}/admin/cashier-shifts/active${branchParam}`, { withCredentials: true });
      if (response.data.has_active_shift) {
        setActiveShift(response.data.shift);
      } else {
        setActiveShift(null);
      }
    } catch (error) {
      console.error("Error fetching active shift:", error);
    }
  };

  const openShift = async () => {
    try {
      const response = await axios.post(`${API}/admin/cashier-shifts/open`,
        {
          initial_cash: parseFloat(initialCash) || 0,
          branch_id: selectedBranch !== 'all' ? selectedBranch : null
        },
        { withCredentials: true }
      );
      setActiveShift(response.data);
      setShowOpenShiftDialog(false);
      setInitialCash("");
      toast.success("Turno abierto correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al abrir turno");
    }
  };

  const closeShift = async () => {
    try {
      const response = await axios.post(`${API}/admin/cashier-shifts/close`,
        { final_cash: parseFloat(finalCash) || 0, notes: shiftNotes },
        { withCredentials: true }
      );
      setShiftReport(response.data);
      setActiveShift(null);
      setShowCloseShiftDialog(false);
      setShowShiftReportDialog(true);
      setFinalCash("");
      setShiftNotes("");
      toast.success("Turno cerrado correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cerrar turno");
    }
  };

  const printShiftReport = () => {
    if (!shiftReport) return;

    const doc = new jsPDF();
    let yPos = 20;

    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("REPORTE DE TURNO", 105, yPos, { align: "center" });
    yPos += 15;

    // Shift info
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Cajero: ${shiftReport.user_name}`, 20, yPos);
    yPos += 7;
    doc.text(`Inicio: ${new Date(shiftReport.start_time).toLocaleString('es-ES')}`, 20, yPos);
    yPos += 7;
    doc.text(`Fin: ${new Date(shiftReport.end_time).toLocaleString('es-ES')}`, 20, yPos);
    yPos += 15;

    // Sales Summary
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("RESUMEN DE VENTAS", 20, yPos);
    yPos += 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Total Pedidos: ${shiftReport.orders_count}`, 25, yPos);
    yPos += 7;
    doc.text(`Ventas Totales: ${shiftReport.total_sales.toFixed(2)} €`, 25, yPos);
    yPos += 7;
    doc.text(`Ventas Efectivo: ${shiftReport.cash_sales.toFixed(2)} €`, 25, yPos);
    yPos += 7;
    doc.text(`Ventas Tarjeta: ${shiftReport.card_sales.toFixed(2)} €`, 25, yPos);
    yPos += 15;

    // Cash Count
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("ARQUEO DE CAJA", 20, yPos);
    yPos += 10;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Caja Inicial: ${shiftReport.initial_cash.toFixed(2)} €`, 25, yPos);
    yPos += 7;
    doc.text(`Efectivo Esperado: ${shiftReport.expected_cash.toFixed(2)} €`, 25, yPos);
    yPos += 7;
    doc.text(`Efectivo Real: ${shiftReport.final_cash.toFixed(2)} €`, 25, yPos);
    yPos += 7;

    // Difference with color
    const diff = shiftReport.cash_difference;
    doc.setFont("helvetica", "bold");
    doc.text(`Diferencia: ${diff >= 0 ? '+' : ''}${diff.toFixed(2)} €`, 25, yPos);
    yPos += 15;

    // Notes
    if (shiftReport.notes) {
      doc.setFont("helvetica", "normal");
      doc.text(`Notas: ${shiftReport.notes}`, 20, yPos);
    }

    doc.save(`turno_${shiftReport.shift_id}.pdf`);
    toast.success("Reporte de turno descargado");
  };

  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportMode, setReportMode] = useState("daily"); // daily, range

  // ... (existing state)

  // Fetch with support for range
  const fetchReport = async (start, end = null) => {
    try {
      setIsRefreshing(true);
      let url = `${API}/admin/cash-report?date=${start}`;
      if (end && reportMode === "range") {
        url += `&end_date=${end}`;
      }
      if (selectedBranch && selectedBranch !== 'all') {
        url += `&branch_id=${selectedBranch}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      setReport(response.data);
    } catch (error) {
      console.error("Error fetching cash report:", error);
      toast.error("Error al cargar el reporte de caja");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`, { withCredentials: true });
      if (response.data?.report_email) {
        setConfigEmail(response.data.report_email);
        setEmailToSend(response.data.report_email);
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };

  useEffect(() => {
    fetchReport(selectedDate, endDate);
    fetchConfig();
    fetchActiveShift();
  }, [selectedDate, endDate, reportMode, selectedBranch]);

  // Handlers for date navigation
  const handlePrevDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    const newDate = date.toISOString().split('T')[0];
    setSelectedDate(newDate);
    if (reportMode === "range") setEndDate(newDate); // Move both if range? better just start
  };

  const handleNextDay = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    const newDate = date.toISOString().split('T')[0];
    setSelectedDate(newDate);
    if (reportMode === "range") setEndDate(newDate);
  };

  const handleToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
    setEndDate(today);
  };

  const formatCurrency = (amount) => `${(amount || 0).toFixed(2)} €`;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  // Export to PDF
  const exportToPDF = () => {
    if (!report) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(251, 191, 36); // Yellow
    doc.rect(0, 0, pageWidth, 40, 'F');

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("Conexo POS", pageWidth / 2, 18, { align: "center" });

    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("Reporte de Caja", pageWidth / 2, 28, { align: "center" });

    doc.setFontSize(12);
    doc.text(formatDateShort(selectedDate), pageWidth / 2, 36, { align: "center" });

    // Reset text color
    doc.setTextColor(0, 0, 0);

    let yPos = 50;

    // Summary Section
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Resumen del Día", 14, yPos);
    yPos += 10;

    doc.autoTable({
      startY: yPos,
      head: [['Concepto', 'Importe']],
      body: [
        ['Ventas Totales', formatCurrency(report.summary?.total_sales)],
        ['Pagos en Efectivo', formatCurrency(report.summary?.total_cash)],
        ['Pagos con Tarjeta', formatCurrency(report.summary?.total_card)],
        ['Pedidos Completados', String(report.summary?.orders_paid || 0)],
        ['Pedidos Pendientes', String(report.summary?.orders_pending || 0)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [251, 191, 36], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 11 },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Local vs Delivery
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Desglose por Tipo de Pedido", 14, yPos);
    yPos += 10;

    doc.autoTable({
      startY: yPos,
      head: [['Tipo', 'Total', 'Efectivo', 'Tarjeta', 'Pedidos']],
      body: [
        ['Local (en restaurante)', formatCurrency(report.local?.total), formatCurrency(report.local?.cash), formatCurrency(report.local?.card), String(report.local?.orders || 0)],
        ['Pickup (recoger)', formatCurrency(report.pickup?.total), formatCurrency(report.pickup?.cash), formatCurrency(report.pickup?.card), String(report.pickup?.orders || 0)],
        ['Delivery (a domicilio)', formatCurrency(report.delivery?.total), formatCurrency(report.delivery?.cash), formatCurrency(report.delivery?.card), String(report.delivery?.orders || 0)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [251, 191, 36], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 10 },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // External Platforms
    if (report.platforms && report.platforms.total_orders > 0) {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Plataformas Externas", 14, yPos);
      yPos += 10;

      doc.autoTable({
        startY: yPos,
        head: [['Plataforma', 'Bruto', 'Comisión', 'Neto', 'Pedidos']],
        body: [
          ['Uber Eats', formatCurrency(report.platforms.ubereats.gross), formatCurrency(report.platforms.ubereats.commission), formatCurrency(report.platforms.ubereats.net), String(report.platforms.ubereats.orders)],
          ['Glovo', formatCurrency(report.platforms.glovo.gross), formatCurrency(report.platforms.glovo.commission), formatCurrency(report.platforms.glovo.net), String(report.platforms.glovo.orders)],
          ['Just Eat', formatCurrency(report.platforms.justeat.gross), formatCurrency(report.platforms.justeat.commission), formatCurrency(report.platforms.justeat.net), String(report.platforms.justeat.orders)],
          ['TOTAL RED', formatCurrency(report.platforms.total_gross), formatCurrency(report.platforms.total_commission), formatCurrency(report.platforms.total_net), String(report.platforms.total_orders)],
        ],
        theme: 'striped',
        headStyles: { fillColor: [147, 51, 234], textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // Delivery Hub
    if (report.hub && report.hub.orders > 0) {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Delivery Hub (Red Externa)", 14, yPos);
      yPos += 10;

      doc.autoTable({
        startY: yPos,
        head: [['Concepto', 'Total', 'Pedidos']],
        body: [
          ['Volumen Gestionado en Hub', formatCurrency(report.hub.total), String(report.hub.orders)],
        ],
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // Drivers Section
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Caja de Repartidores", 14, yPos);
    yPos += 10;

    doc.autoTable({
      startY: yPos,
      head: [['Concepto', 'Importe']],
      body: [
        ['Efectivo Cobrado', formatCurrency(report.drivers?.cash_collected)],
        ['Efectivo Entregado', formatCurrency(report.drivers?.cash_delivered)],
        ['Pendiente por Entregar', formatCurrency(report.drivers?.cash_pending)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [251, 191, 36], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 11 },
    });

    // Driver shifts if any
    if (report.drivers?.shifts?.length > 0) {
      yPos = doc.lastAutoTable.finalY + 10;

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Detalle de Turnos", 14, yPos);
      yPos += 8;

      doc.autoTable({
        startY: yPos,
        head: [['Repartidor', 'Horario', 'Entregas', 'Cobrado', 'Entregado a', 'Estado']],
        body: report.drivers.shifts.map(shift => [
          shift.driver_name,
          `${formatTime(shift.start_time)} - ${shift.end_time ? formatTime(shift.end_time) : 'En turno'}`,
          String(shift.orders_count),
          formatCurrency(shift.cash_collected),
          shift.delivered_to || '-',
          shift.status === 'active' ? 'Activo' : 'Cerrado'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [100, 100, 100], textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9 },
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Generado el ${new Date().toLocaleString('es-ES')} - Página ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Save
    doc.save(`reporte-caja-${selectedDate}.pdf`);
    toast.success("PDF exportado correctamente");
  };

  // Imprimir Reporte en Tiquetera (vía ZT-Bridge)
  const printTicketReport = async () => {
    if (!report) return;

    try {
      // Mapear datos al formato esperado por PrintService_escpos
      const reportData = {
        total_sales: report.summary?.total_sales || 0,
        total_orders: report.summary?.orders_paid || 0,
        pending_orders: report.summary?.orders_pending || 0,
        average: (report.summary?.total_sales || 0) / (report.summary?.orders_paid || 1)
      };

      const result = await printService.printOrder(reportData, 'Conexo POS', null, 'report');

      if (result.success) {
        toast.success("Reporte enviado a la tiquetera");
      } else {
        throw new Error(result.error);
      }
    } catch (e) {
      console.error("Error al imprimir reporte:", e);
      toast.error("Error al imprimir reporte. ¿Está abierto ZT-Bridge?");
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!report) return;

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['REPORTE DE CAJA - Conexo POS'],
      ['Fecha:', formatDateShort(selectedDate)],
      [''],
      ['RESUMEN DEL DÍA'],
      ['Concepto', 'Importe'],
      ['Ventas Totales', report.summary?.total_sales || 0],
      ['Pagos en Efectivo', report.summary?.total_cash || 0],
      ['Pagos con Tarjeta', report.summary?.total_card || 0],
      ['Pedidos Completados', report.summary?.orders_paid || 0],
      ['Pedidos Pendientes', report.summary?.orders_pending || 0],
      [''],
      ['DESGLOSE POR TIPO DE PEDIDO'],
      ['Tipo', 'Total', 'Efectivo', 'Tarjeta', 'Pedidos'],
      ['Local (en restaurante)', report.local?.total || 0, report.local?.cash || 0, report.local?.card || 0, report.local?.orders || 0],
      ['Recoger (pickup)', report.pickup?.total || 0, report.pickup?.cash || 0, report.pickup?.card || 0, report.pickup?.orders || 0],
      ['Delivery (a domicilio)', report.delivery?.total || 0, report.delivery?.cash || 0, report.delivery?.card || 0, report.delivery?.orders || 0],
      [''],
      ['PLATAFORMAS EXTERNAS (INTEGRACIONES)'],
      ['Plataforma', 'Bruto', 'Comisión', 'Neto', 'Pedidos'],
      ['Uber Eats', report.platforms?.ubereats.gross || 0, report.platforms?.ubereats.commission || 0, report.platforms?.ubereats.net || 0, report.platforms?.ubereats.orders || 0],
      ['Glovo', report.platforms?.glovo.gross || 0, report.platforms?.glovo.commission || 0, report.platforms?.glovo.net || 0, report.platforms?.glovo.orders || 0],
      ['Just Eat', report.platforms?.justeat.gross || 0, report.platforms?.justeat.commission || 0, report.platforms?.justeat.net || 0, report.platforms?.justeat.orders || 0],
      ['TOTAL RED', report.platforms?.total_gross || 0, report.platforms?.total_commission || 0, report.platforms?.total_net || 0, report.platforms?.total_orders || 0],
      [''],
      ['DELIVERY HUB (RED RIDER EXTERNA)'],
      ['Concepto', 'Total', 'Pedidos'],
      ['Volumen Red Externa', report.hub?.total || 0, report.hub?.orders || 0],
      [''],
      ['CAJA DE REPARTIDORES INTERNOS'],
      ['Concepto', 'Importe'],
      ['Efectivo Cobrado', report.drivers?.cash_collected || 0],
      ['Efectivo Entregado', report.drivers?.cash_delivered || 0],
      ['Pendiente por Entregar', report.drivers?.cash_pending || 0],
    ];

    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);

    // Set column widths
    summaryWs['!cols'] = [
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
    ];

    XLSX.utils.book_append_sheet(wb, summaryWs, 'Resumen');

    // Driver shifts sheet if any
    if (report.drivers?.shifts?.length > 0) {
      const shiftsData = [
        ['DETALLE DE TURNOS DE REPARTIDORES'],
        ['Fecha:', formatDateShort(selectedDate)],
        [''],
        ['Repartidor', 'Inicio', 'Fin', 'Entregas', 'Efectivo Cobrado', 'Entregado a', 'Estado'],
        ...report.drivers.shifts.map(shift => [
          shift.driver_name,
          formatTime(shift.start_time),
          shift.end_time ? formatTime(shift.end_time) : 'En turno',
          shift.orders_count,
          shift.cash_collected || 0,
          shift.delivered_to || '-',
          shift.status === 'active' ? 'Activo' : 'Cerrado'
        ])
      ];

      const shiftsWs = XLSX.utils.aoa_to_sheet(shiftsData);
      shiftsWs['!cols'] = [
        { wch: 20 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 15 },
        { wch: 20 },
        { wch: 10 },
      ];

      XLSX.utils.book_append_sheet(wb, shiftsWs, 'Turnos');
    }

    // Hub jobs sheet if any
    if (report.hub?.jobs?.length > 0) {
      const hubData = [
        ['DETALLE DE REPARTOS EXTERNOS (DELIVERY HUB)'],
        ['Fecha:', formatDateShort(selectedDate)],
        [''],
        ['Pedido', 'Repartidor', 'Estado', 'Total'],
        ...report.hub.jobs.map(job => [
          job.order_id,
          job.rider_name,
          job.status,
          job.total || 0
        ])
      ];

      const hubWs = XLSX.utils.aoa_to_sheet(hubData);
      hubWs['!cols'] = [
        { wch: 25 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
      ];

      XLSX.utils.book_append_sheet(wb, hubWs, 'Delivery Hub');
    }

    // Generate and save
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `reporte-caja-${selectedDate}.xlsx`);
    toast.success("Excel exportado correctamente");
  };

  // Send report via email
  const sendReportByEmail = async () => {
    if (!emailToSend || !emailToSend.includes("@")) {
      toast.error("Introduce un email válido");
      return;
    }

    setIsSendingEmail(true);

    try {
      const response = await axios.post(
        `${API}/admin/send-daily-report`,
        { email: emailToSend, date: selectedDate },
        { withCredentials: true }
      );

      toast.success(response.data.message || "Reporte enviado correctamente");
      setShowEmailDialog(false);

      // Save email to config if different
      if (emailToSend !== configEmail) {
        try {
          await axios.put(
            `${API}/config`,
            { report_email: emailToSend },
            { withCredentials: true }
          );
          setConfigEmail(emailToSend);
        } catch (e) {
          console.log("Could not save email to config");
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al enviar el reporte";
      toast.error(message);
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Caja">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Caja">
      <div className="space-y-6">
        {/* Shift Management Banner */}
        <Card className={`border-2 ${activeShift ? 'bg-green-900/20 border-green-500' : 'bg-yellow-900/20 border-yellow-500'}`}>
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${activeShift ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                  {activeShift ? (
                    <Clock className="w-6 h-6 text-green-500" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-yellow-500" />
                  )}
                </div>
                <div>
                  {activeShift ? (
                    <>
                      <p className="text-green-400 font-semibold">Turno Activo</p>
                      <p className="text-sm text-gray-400">
                        Inicio: {new Date(activeShift.start_time).toLocaleTimeString()} •
                        Ventas: {activeShift.current_sales?.total?.toFixed(2) || '0.00'} € ({activeShift.current_sales?.orders_count || 0} pedidos)
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-yellow-400 font-semibold">Sin turno activo</p>
                      <p className="text-sm text-gray-400">Abre un turno para comenzar a registrar ventas</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                {activeShift ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchActiveShift}
                      className="border-gray-600 text-gray-300"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Actualizar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setShowCloseShiftDialog(true)}
                      className="bg-red-600 hover:bg-red-500 text-white"
                      data-testid="close-shift-btn"
                    >
                      <Square className="w-4 h-4 mr-1" />
                      Cerrar Turno
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setShowOpenShiftDialog(true)}
                    className="bg-green-600 hover:bg-green-500 text-white"
                    data-testid="open-shift-btn"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Abrir Turno
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Header with Date Navigation */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">Reporte de Caja</h1>
            <p className="text-gray-400 capitalize">
              {reportMode === "range"
                ? `${formatDateShort(selectedDate)} - ${formatDateShort(endDate)}`
                : formatDate(selectedDate)
              }
            </p>

          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-wrap">
            {/* Branch Selector */}
            {((user?.role === "admin" && (!user?.assigned_branches || user?.assigned_branches.length !== 1)) || (user?.assigned_branches?.length > 1)) && (
              <div className="mr-2 mb-2 sm:mb-0">
                <Select value={selectedBranch || "all"} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="w-[180px] bg-gray-900 border-gray-700 text-white">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <SelectValue placeholder="Todas las sucursales" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las sucursales</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.branch_id} value={b.branch_id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Mode Toggle */}
            <div className="bg-gray-800 p-1 rounded-lg flex mr-2 mb-2 sm:mb-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReportMode("daily")}
                className={`text-xs h-8 px-3 ${reportMode === "daily" ? "bg-gray-700 text-white shadow-sm font-bold" : "text-gray-400 hover:text-white"}`}
              >
                Diario
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReportMode("range")}
                className={`text-xs h-8 px-3 ${reportMode === "range" ? "bg-gray-700 text-white shadow-sm font-bold" : "text-gray-400 hover:text-white"}`}
              >
                Rango
              </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrevDay}
                className="border-gray-700 text-gray-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              {/* Calendar Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={isToday && reportMode === "daily" ? "default" : "outline"}
                    size="sm"
                    className={isToday && reportMode === "daily" ? "bg-yellow-500 text-black hover:bg-yellow-400 font-medium" : "border-gray-700 text-gray-400 hover:text-white"}
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {reportMode === "daily" ? (isToday ? "Hoy" : formatDate(selectedDate)) : formatDateShort(selectedDate)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-gray-900 border-gray-700" align="center">
                  <Calendar
                    mode="single"
                    selected={new Date(selectedDate)}
                    onSelect={(date) => {
                      if (date) {
                        setSelectedDate(date.toISOString().split('T')[0]);
                      }
                    }}
                    disabled={(date) => date > new Date()}
                    initialFocus
                    className="bg-gray-900 text-white"
                  />
                </PopoverContent>
              </Popover>

              {/* End Date Picker (Only for Range) */}
              {reportMode === "range" && (
                <>
                  <span className="text-gray-500 font-bold">-</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-400 hover:text-white"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {formatDateShort(endDate)}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-gray-900 border-gray-700" align="center">
                      <Calendar
                        mode="single"
                        selected={new Date(endDate)}
                        onSelect={(date) => {
                          if (date) {
                            setEndDate(date.toISOString().split('T')[0]);
                          }
                        }}
                        disabled={(date) => date > new Date()}
                        initialFocus
                        className="bg-gray-900 text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </>
              )}

              <Button
                variant="outline"
                size="icon"
                onClick={handleNextDay}
                disabled={isToday && reportMode === "daily"}
                className="border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={() => fetchReport(selectedDate, endDate)}
                disabled={isRefreshing}
                className="border-gray-700 text-gray-400 hover:text-white ml-2 bg-gray-800 hover:bg-gray-700"
                title="Actualizar reporte"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Botón de Impresión Directa (Prioridad) */}
            <Button
              onClick={printTicketReport}
              className="bg-yellow-500 hover:bg-yellow-400 text-black ml-2 font-bold"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir Reporte
            </Button>

            {/* Otros Exportar (Menos visibles) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gray-700 text-gray-400 hover:text-white ml-2"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-gray-900 border-gray-800">
                <DropdownMenuItem
                  onClick={exportToPDF}
                  className="text-white hover:bg-gray-800 cursor-pointer"
                >
                  <FileText className="w-4 h-4 mr-2 text-red-500" />
                  Exportar a PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={exportToExcel}
                  className="text-white hover:bg-gray-800 cursor-pointer"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-500" />
                  Exportar a Excel
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-gray-800" />
                <DropdownMenuItem
                  onClick={() => setShowEmailDialog(true)}
                  className="text-white hover:bg-gray-800 cursor-pointer"
                >
                  <Mail className="w-4 h-4 mr-2 text-blue-500" />
                  Enviar por Email
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Summary Card */}
        <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 border-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-black flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Total del Día
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-black/70 text-sm">Ventas Totales</p>
                <p className="text-3xl font-bold text-black">{formatCurrency(report?.summary?.total_sales)}</p>
              </div>
              <div>
                <p className="text-black/70 text-sm flex items-center gap-1">
                  <Banknote className="w-4 h-4" /> Efectivo
                </p>
                <p className="text-2xl font-bold text-black">{formatCurrency(report?.summary?.total_cash)}</p>
              </div>
              <div>
                <p className="text-black/70 text-sm flex items-center gap-1">
                  <CreditCard className="w-4 h-4" /> Tarjeta
                </p>
                <p className="text-2xl font-bold text-black">{formatCurrency(report?.summary?.total_card)}</p>
              </div>
              <div>
                <p className="text-black/70 text-sm">Pedidos</p>
                <p className="text-2xl font-bold text-black">
                  {report?.summary?.orders_paid || 0}
                  {report?.summary?.orders_pending > 0 && (
                    <span className="text-sm font-normal ml-1">
                      (+{report?.summary?.orders_pending} pendientes)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Local vs Delivery vs Pickup */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Local Sales */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <UtensilsCrossed className="w-5 h-5 text-green-500" />
                Ventas en Local
                <Badge className="bg-green-500/20 text-green-400 ml-auto whitespace-nowrap text-xs">
                  {report?.local?.orders || 0} pedidos
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Total Local</span>
                  <span className="text-2xl font-bold text-white">{formatCurrency(report?.local?.total)}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-800">
                  <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="text-xs text-gray-500">Efectivo</p>
                      <p className="font-semibold text-white">{formatCurrency(report?.local?.cash)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <div>
                      <p className="text-xs text-gray-500">Tarjeta</p>
                      <p className="font-semibold text-white">{formatCurrency(report?.local?.card)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pickup Sales */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <ShoppingBag className="w-5 h-5 text-yellow-500" />
                Recoger en Tienda
                <Badge className="bg-yellow-500/20 text-yellow-400 ml-auto whitespace-nowrap text-xs">
                  {report?.pickup?.orders || 0} pedidos
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Total Pickup</span>
                  <span className="text-2xl font-bold text-white">{formatCurrency(report?.pickup?.total)}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-800">
                  <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="text-xs text-gray-500">Efectivo</p>
                      <p className="font-semibold text-white">{formatCurrency(report?.pickup?.cash)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <div>
                      <p className="text-xs text-gray-500">Tarjeta</p>
                      <p className="font-semibold text-white">{formatCurrency(report?.pickup?.card)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Sales */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Truck className="w-5 h-5 text-blue-500" />
                Ventas a Domicilio
                <Badge className="bg-blue-500/20 text-blue-400 ml-auto whitespace-nowrap text-xs">
                  {report?.delivery?.orders || 0} pedidos
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Total Delivery</span>
                  <span className="text-2xl font-bold text-white">{formatCurrency(report?.delivery?.total)}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-800">
                  <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="text-xs text-gray-500">Efectivo</p>
                      <p className="font-semibold text-white">{formatCurrency(report?.delivery?.cash)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <div>
                      <p className="text-xs text-gray-500">Tarjeta</p>
                      <p className="font-semibold text-white">{formatCurrency(report?.delivery?.card)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* External Platform Integrations Section */}
        {report?.platforms && report.platforms.total_orders > 0 && (
          <Card className="bg-gray-900 border border-purple-500/20 overflow-hidden">
            <CardHeader className="bg-purple-500/5">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Badge className="bg-purple-600 text-white font-black px-2">INTEGRACIONES</Badge>
                Plataformas Externas (Uber, Glovo, Just Eat)
                <span className="ml-auto text-sm font-normal text-gray-400">
                  Total Red: <strong className="text-white">{formatCurrency(report.platforms.total_gross)}</strong>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Uber Eat */}
                <div className="p-4 bg-gray-800/40 rounded-xl border border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center font-black text-xs">U</div>
                    <span className="font-bold text-white">Uber Eats</span>
                    <Badge variant="outline" className="ml-auto border-gray-600">{report.platforms.ubereats.orders} ped</Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Bruto:</span>
                      <span className="text-white font-bold">{formatCurrency(report.platforms.ubereats.gross)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-red-400">
                      <span>Comisión:</span>
                      <span>-{formatCurrency(report.platforms.ubereats.commission)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-1 border-t border-gray-700 mt-1 uppercase font-black tracking-tighter">
                      <span className="text-gray-400">Neto:</span>
                      <span className="text-green-400">{formatCurrency(report.platforms.ubereats.net)}</span>
                    </div>
                  </div>
                </div>

                {/* Glovo */}
                <div className="p-4 bg-gray-800/40 rounded-xl border border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center font-black text-xs text-black">G</div>
                    <span className="font-bold text-white">Glovo</span>
                    <Badge variant="outline" className="ml-auto border-gray-600">{report.platforms.glovo.orders} ped</Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Bruto:</span>
                      <span className="text-white font-bold">{formatCurrency(report.platforms.glovo.gross)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-red-400">
                      <span>Comisión:</span>
                      <span>-{formatCurrency(report.platforms.glovo.commission)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-1 border-t border-gray-700 mt-1 uppercase font-black tracking-tighter">
                      <span className="text-gray-400">Neto:</span>
                      <span className="text-green-400">{formatCurrency(report.platforms.glovo.net)}</span>
                    </div>
                  </div>
                </div>

                {/* Just Eat */}
                <div className="p-4 bg-gray-800/40 rounded-xl border border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center font-black text-xs">J</div>
                    <span className="font-bold text-white">Just Eat</span>
                    <Badge variant="outline" className="ml-auto border-gray-600">{report.platforms.justeat.orders} ped</Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Bruto:</span>
                      <span className="text-white font-bold">{formatCurrency(report.platforms.justeat.gross)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-red-400">
                      <span>Comisión:</span>
                      <span>-{formatCurrency(report.platforms.justeat.commission)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-1 border-t border-gray-700 mt-1 uppercase font-black tracking-tighter">
                      <span className="text-gray-400">Neto:</span>
                      <span className="text-green-400">{formatCurrency(report.platforms.justeat.net)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delivery Hub Section */}
        {report?.hub && report.hub.orders > 0 && (
          <Card className="bg-gray-900 border border-blue-500/20 overflow-hidden">
            <CardHeader className="bg-blue-500/5">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Badge className="bg-blue-600 text-white font-black px-2">DELIVERY HUB</Badge>
                Red de Riders Externa (Pago por Servicio)
                <span className="ml-auto text-sm font-normal text-gray-400">
                  Total Hub: <strong className="text-white">{formatCurrency(report.hub.total)}</strong>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="flex items-center justify-between p-4 bg-blue-900/10 rounded-xl border border-blue-500/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Truck className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">{report.hub.orders} Pedidos Despachados</p>
                    <p className="text-sm text-gray-400">Gestionados por la red de conductores externa.</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 uppercase font-black">Volumen de Red</p>
                  <p className="text-2xl font-black text-blue-400">{formatCurrency(report.hub.total)}</p>
                </div>
              </div>

              {/* Detalle de Repartidores Hub */}
              {report.hub.jobs?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-400">Detalle de repartos externos</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 border-b border-gray-800">
                          <th className="text-left py-2 font-medium">Pedido</th>
                          <th className="text-left py-2 font-medium">Repartidor</th>
                          <th className="text-center py-2 font-medium">Estado</th>
                          <th className="text-right py-2 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.hub.jobs.map((job) => (
                          <tr key={job.job_id} className="border-b border-gray-800/50 hover:bg-white/5">
                            <td className="py-2 text-white font-mono text-xs">#{job.order_id?.split('_')[1] || job.order_id}</td>
                            <td className="py-2 text-gray-300">{job.rider_name}</td>
                            <td className="py-2 text-center">
                              <Badge variant="outline" className={`text-[10px] py-0 h-5 ${job.status === 'delivered' ? 'border-green-500 text-green-500' :
                                job.status === 'cancelled' ? 'border-red-500 text-red-500' :
                                  'border-blue-500 text-blue-500'
                                }`}>
                                {job.status}
                              </Badge>
                            </td>
                            <td className="py-2 text-right text-white">{formatCurrency(job.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Drivers Cash Summary */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-yellow-500" />
              Caja de Repartidores <span className="text-xs font-black bg-yellow-500/20 text-yellow-500 px-2 rounded-full ml-2">INTERNOS</span>
            </CardTitle>
            <p className="text-xs text-gray-500">Métricas exclusivas para el personal de reparto propio del restaurante.</p>
          </CardHeader>
          <CardContent>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-800 rounded-xl">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Efectivo Cobrado</p>
                <p className="text-xl font-bold text-white">{formatCurrency(report?.drivers?.cash_collected)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Efectivo Entregado</p>
                <p className="text-xl font-bold text-green-500">{formatCurrency(report?.drivers?.cash_delivered)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Pendiente por Entregar</p>
                <p className="text-xl font-bold text-orange-500">{formatCurrency(report?.drivers?.cash_pending)}</p>
              </div>
            </div>

            {/* Driver Shifts List */}
            {report?.drivers?.shifts?.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Turnos del día</h4>
                {report.drivers.shifts.map((shift) => (
                  <div
                    key={shift.shift_id}
                    className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${shift.status === "active" ? "bg-green-500/20" : "bg-gray-700"
                        }`}>
                        <Truck className={`w-5 h-5 ${shift.status === "active" ? "text-green-500" : "text-gray-500"
                          }`} />
                      </div>
                      <div>
                        <p className="font-medium text-white">{shift.driver_name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {formatTime(shift.start_time)} - {shift.end_time ? formatTime(shift.end_time) : "En turno"}
                          <span className="mx-1">•</span>
                          {shift.orders_count} entregas
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        {shift.status === "active" ? (
                          <Badge className="bg-green-500/20 text-green-400">
                            <Clock className="w-3 h-3 mr-1" />
                            Activo
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-700 text-gray-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Cerrado
                          </Badge>
                        )}
                      </div>
                      <p className="text-lg font-bold text-white mt-1">
                        {formatCurrency(shift.cash_collected)}
                      </p>
                      {shift.status === "closed" && shift.delivered_to && (
                        <p className="text-xs text-gray-500">
                          Entregado a: {shift.delivered_to}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-800 rounded-xl">
                <User className="w-12 h-12 text-gray-700 mx-auto mb-2" />
                <p className="text-gray-500">No hay turnos de repartidores internos registrados hoy.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Enviar Reporte por Email
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              El reporte del {formatDate(selectedDate)} será enviado al email indicado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Email destinatario</label>
              <Input
                type="email"
                placeholder="correo@ejemplo.com"
                value={emailToSend}
                onChange={(e) => setEmailToSend(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="email-report-input"
              />
              {configEmail && configEmail !== emailToSend && (
                <p className="text-xs text-gray-500 mt-1">
                  Email guardado: {configEmail}
                </p>
              )}
            </div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-sm text-gray-400 mb-2">El reporte incluirá:</p>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Resumen de ventas del día</li>
                <li>• Desglose Local vs Delivery</li>
                <li>• Caja de repartidores</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEmailDialog(false)}
              className="border-gray-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={sendReportByEmail}
              disabled={isSendingEmail || !emailToSend}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="send-email-btn"
            >
              {isSendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Reporte
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Open Shift Dialog */}
      <Dialog open={showOpenShiftDialog} onOpenChange={setShowOpenShiftDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-500" />
              Abrir Turno
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Ingresa la cantidad de efectivo inicial en caja para comenzar el turno.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Efectivo inicial en caja (€)</label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={initialCash}
                onChange={(e) => setInitialCash(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white text-lg"
                data-testid="initial-cash-input"
              />
            </div>
            <div className="bg-green-900/20 border border-green-500/30 p-4 rounded-lg">
              <p className="text-sm text-green-400">
                Al abrir el turno, todas las ventas se registrarán bajo tu usuario hasta que cierres el turno.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowOpenShiftDialog(false)}
              className="border-gray-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={openShift}
              className="bg-green-600 hover:bg-green-500"
              data-testid="confirm-open-shift-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Abrir Turno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Shift Dialog */}
      <Dialog open={showCloseShiftDialog} onOpenChange={setShowCloseShiftDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Square className="w-5 h-5 text-red-500" />
              Cerrar Turno
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Cuenta el efectivo en caja y cierra tu turno para generar el reporte.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {activeShift && (
              <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Caja inicial:</span>
                  <span className="text-white">{activeShift.initial_cash?.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Ventas en efectivo:</span>
                  <span className="text-green-400">+{activeShift.current_sales?.cash?.toFixed(2) || '0.00'} €</span>
                </div>
                <div className="flex justify-between text-sm font-semibold border-t border-gray-700 pt-2">
                  <span className="text-gray-300">Efectivo esperado:</span>
                  <span className="text-yellow-400">
                    {((activeShift.initial_cash || 0) + (activeShift.current_sales?.cash || 0)).toFixed(2)} €
                  </span>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Efectivo real en caja (€)</label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={finalCash}
                onChange={(e) => setFinalCash(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white text-lg"
                data-testid="final-cash-input"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Notas (opcional)</label>
              <Textarea
                placeholder="Observaciones del turno..."
                value={shiftNotes}
                onChange={(e) => setShiftNotes(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCloseShiftDialog(false)}
              className="border-gray-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={closeShift}
              className="bg-red-600 hover:bg-red-500"
              data-testid="confirm-close-shift-btn"
            >
              <Square className="w-4 h-4 mr-2" />
              Cerrar Turno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Report Dialog */}
      <Dialog open={showShiftReportDialog} onOpenChange={setShowShiftReportDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Turno Cerrado
            </DialogTitle>
          </DialogHeader>
          {shiftReport && (
            <div className="space-y-4 py-4">
              <div className="text-center pb-4 border-b border-gray-800">
                <p className="text-gray-400 text-sm">Cajero</p>
                <p className="text-xl font-bold text-white">{shiftReport.user_name}</p>
                <p className="text-xs text-gray-500">
                  {new Date(shiftReport.start_time).toLocaleTimeString()} - {new Date(shiftReport.end_time).toLocaleTimeString()}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Pedidos</p>
                  <p className="text-2xl font-bold text-white">{shiftReport.orders_count}</p>
                </div>
                <div className="bg-gray-800 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Ventas Totales</p>
                  <p className="text-2xl font-bold text-green-400">{shiftReport.total_sales.toFixed(2)} €</p>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Efectivo:</span>
                  <span className="text-white">{shiftReport.cash_sales.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Tarjeta:</span>
                  <span className="text-white">{shiftReport.card_sales.toFixed(2)} €</span>
                </div>
              </div>

              <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                <p className="text-sm font-semibold text-gray-300">Arqueo de Caja</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Caja inicial:</span>
                  <span className="text-white">{shiftReport.initial_cash.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Efectivo esperado:</span>
                  <span className="text-white">{shiftReport.expected_cash.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Efectivo real:</span>
                  <span className="text-white">{shiftReport.final_cash.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm font-semibold border-t border-gray-700 pt-2">
                  <span className="text-gray-300">Diferencia:</span>
                  <span className={shiftReport.cash_difference >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {shiftReport.cash_difference >= 0 ? '+' : ''}{shiftReport.cash_difference.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowShiftReportDialog(false)}
              className="border-gray-700 w-full sm:w-auto"
            >
              Cerrar
            </Button>
            <Button
              onClick={printShiftReport}
              className="bg-yellow-500 hover:bg-yellow-400 text-black w-full sm:w-auto"
              data-testid="print-shift-report-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir Reporte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout >
  );
}
